import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import SourcesLegendControl from '@App/components/Map/controls/SourcesLegendControl/SourcesLegendControl.jsx';

import {
  getSourceColorExpression,
  layers,
  sources,
} from './sources.layers';

import './sources.css';

function Sources({ map }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!map) return undefined;

    const isSourcesOverview = pathname.replace(/\/$/, '') === '/bronnen';
    if (!isSourcesOverview) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    let selectedMode = 'potential';
    let isHoveringFeature = false;
    const setSourceColors = (mode, dimmed = isHoveringFeature) => {
      selectedMode = mode;
      map.setPaintProperty(
        layers.flowingWater.id,
        'line-color',
        getSourceColorExpression(mode, 'flowingWater', dimmed),
      );
      map.setPaintProperty(
        layers.stillWater.id,
        'fill-color',
        getSourceColorExpression(mode, 'stillWater', dimmed),
      );
      map.setPaintProperty(
        layers.stillWaterOutline.id,
        'line-color',
        getSourceColorExpression(mode, 'stillWater', dimmed),
      );
      map.setPaintProperty(
        layers.rwzi.id,
        'circle-color',
        getSourceColorExpression(mode, 'rwzi', dimmed),
      );
    };
    const legendControl = isSourcesOverview
      ? new SourcesLegendControl(setSourceColors)
      : undefined;
    if (legendControl) map.addControl(legendControl, 'bottom-right');

    Object.values(sources).forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    Object.values(layers).forEach((layer) => {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer, layerAfterRoadLabelsId);
      }
    });

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });
    const setFeatureHoverState = (source, sourceLayer, featureId, hover) => {
      if (featureId !== undefined && featureId !== null) {
        map.setFeatureState({ source, sourceLayer, id: featureId }, { hover });
      }
    };
    const hoverTargets = [
      {
        layerId: layers.flowingWaterHitArea.id,
        source: sources.flowingWater.id,
        sourceLayer: 'Stromend_water',
      },
      {
        layerId: layers.stillWater.id,
        source: sources.stillWater.id,
        sourceLayer: 'Stilstaand_water',
      },
      {
        layerId: layers.rwzi.id,
        source: sources.rwzi.id,
        sourceLayer: 'RWZI',
      },
    ];
    const hoverHandlers = isSourcesOverview
      ? hoverTargets.map(({ layerId, source, sourceLayer }) => {
          let hoveredFeatureId;
          const handleLayerMouseMove = (event) => {
            const featureId = event.features?.[0]?.id;
            if (featureId === undefined || featureId === null || hoveredFeatureId === featureId) return;
            if (!isHoveringFeature) {
              setSourceColors(selectedMode, true);
              isHoveringFeature = true;
            }
            setFeatureHoverState(source, sourceLayer, hoveredFeatureId, false);
            setFeatureHoverState(source, sourceLayer, featureId, true);
            hoveredFeatureId = featureId;
          };
          const handleLayerMouseLeave = () => {
            setFeatureHoverState(source, sourceLayer, hoveredFeatureId, false);
            hoveredFeatureId = undefined;
            setSourceColors(selectedMode, false);
            isHoveringFeature = false;
          };
          map.on('mousemove', layerId, handleLayerMouseMove);
          map.on('mouseleave', layerId, handleLayerMouseLeave);
          return { layerId, handleLayerMouseMove, handleLayerMouseLeave };
        })
      : [];
    const interactiveLayerIds = Object.keys(navigationTargets);
    const handleMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      });
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      if (legendControl) map.removeControl(legendControl);
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      hoverHandlers.forEach(({ layerId, handleLayerMouseMove, handleLayerMouseLeave }) => {
        map.off('mousemove', layerId, handleLayerMouseMove);
        map.off('mouseleave', layerId, handleLayerMouseLeave);
      });
      map.off('mousemove', handleMouseMove);
      map.getCanvas().style.cursor = '';
      Object.values(layers).forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      Object.values(sources).forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map, navigate, pathname]);

  return <Outlet context={{ map }} />;
}

export default Sources;
